import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { apiClient } from '../api/client'
import { Card } from '../components/ui/Card'
import { Modal } from '../components/ui/Modal'
import { Bell, Phone, User, Trash2, Check, AlertTriangle } from 'lucide-react'
import { formatPhone } from '../lib/utils'
import type { UserSettings } from '../types'

export function SettingsPage() {
  const { t } = useTranslation()
  const { user, logout, refreshUser } = useAuth()

  const [settings, setSettings] = useState<UserSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Form states
  const [notificationType, setNotificationType] = useState<'alimtalk' | 'alimtalk_with_link'>('alimtalk')
  const [name, setName] = useState('')

  // Modal states
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [withdrawPassword, setWithdrawPassword] = useState('')
  const [withdrawError, setWithdrawError] = useState('')
  const [isWithdrawing, setIsWithdrawing] = useState(false)

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      const data = await apiClient.getSettings()
      setSettings(data)
      setNotificationType(data.notification_type)
      setName(data.name)
    } catch (error) {
      console.error('Failed to fetch settings:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveNotificationType = async (type: 'alimtalk' | 'alimtalk_with_link') => {
    setIsSaving(true)
    try {
      await apiClient.updateNotificationType(type)
      setNotificationType(type)
      showSaveSuccess()
    } catch (error) {
      console.error('Failed to update notification type:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveName = async () => {
    if (!name.trim()) return

    setIsSaving(true)
    try {
      await apiClient.updateName(name)
      await refreshUser()
      showSaveSuccess()
    } catch (error) {
      console.error('Failed to update name:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleWithdraw = async () => {
    if (!withdrawPassword) {
      setWithdrawError(t('settings.enterPassword'))
      return
    }

    setIsWithdrawing(true)
    setWithdrawError('')

    try {
      await apiClient.withdrawAccount(withdrawPassword)
      logout()
      window.location.href = '/login'
    } catch (error) {
      setWithdrawError(error instanceof Error ? error.message : 'Failed to withdraw account')
    } finally {
      setIsWithdrawing(false)
    }
  }

  const showSaveSuccess = () => {
    setSaveSuccess(true)
    setTimeout(() => setSaveSuccess(false), 2000)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('settings.title')}</h1>
          <p className="text-light-text-muted dark:text-dark-text-muted mt-1">
            알림 및 계정 설정을 관리하세요
          </p>
        </div>

        {saveSuccess && (
          <div className="flex items-center gap-2 text-success">
            <Check className="w-5 h-5" />
            <span>{t('settings.saved')}</span>
          </div>
        )}
      </div>

      {/* Notification Settings */}
      <Card title={t('settings.notification')} subtitle="알림톡 발송 방식을 설정합니다">
        <div className="space-y-6">
          {/* Notification Type */}
          <div>
            <label className="label">{t('settings.notificationType')}</label>
            <div className="space-y-2 mt-2">
              <label className="flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors hover:border-primary/50"
                style={{
                  borderColor: notificationType === 'alimtalk' ? 'var(--color-primary)' : undefined,
                  backgroundColor: notificationType === 'alimtalk' ? 'rgba(59, 130, 246, 0.05)' : undefined,
                }}
              >
                <input
                  type="radio"
                  name="notificationType"
                  value="alimtalk"
                  checked={notificationType === 'alimtalk'}
                  onChange={() => handleSaveNotificationType('alimtalk')}
                  className="w-4 h-4 text-primary"
                />
                <Bell className="w-5 h-5 text-primary" />
                <div className="flex-1">
                  <p className="font-medium">{t('settings.alimtalkOnly')}</p>
                  <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                    텍스트 알림만 발송합니다
                  </p>
                </div>
              </label>

              <label className="flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors hover:border-primary/50"
                style={{
                  borderColor: notificationType === 'alimtalk_with_link' ? 'var(--color-primary)' : undefined,
                  backgroundColor: notificationType === 'alimtalk_with_link' ? 'rgba(59, 130, 246, 0.05)' : undefined,
                }}
              >
                <input
                  type="radio"
                  name="notificationType"
                  value="alimtalk_with_link"
                  checked={notificationType === 'alimtalk_with_link'}
                  onChange={() => handleSaveNotificationType('alimtalk_with_link')}
                  className="w-4 h-4 text-primary"
                />
                <Bell className="w-5 h-5 text-primary" />
                <div className="flex-1">
                  <p className="font-medium">{t('settings.alimtalkWithLink')}</p>
                  <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
                    스크린샷 링크가 포함된 알림을 발송합니다
                  </p>
                </div>
              </label>
            </div>
          </div>
        </div>
      </Card>

      {/* Account Settings */}
      <Card title={t('settings.account')} subtitle="계정 정보를 관리합니다">
        <div className="space-y-6">
          {/* Name */}
          <div>
            <label className="label">{t('settings.updateName')}</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-light-text-muted dark:text-dark-text-muted" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input pl-10"
                  placeholder="이름"
                />
              </div>
              <button
                onClick={handleSaveName}
                disabled={isSaving || name === settings?.name}
                className="btn btn-primary"
              >
                {t('common.save')}
              </button>
            </div>
          </div>

          {/* Current Phone */}
          <div>
            <label className="label">{t('settings.currentPhone')}</label>
            <div className="p-4 rounded-lg bg-light-bg-secondary dark:bg-dark-bg-secondary">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-light-text-muted dark:text-dark-text-muted" />
                <span>{formatPhone(user?.phone || '')}</span>
              </div>
            </div>
            <p className="text-sm text-light-text-muted dark:text-dark-text-muted mt-2">
              전화번호 변경은 고객센터에 문의해주세요
            </p>
          </div>

          {/* Danger Zone */}
          <div className="pt-6 border-t border-light-border dark:border-dark-border">
            <div className="flex items-start gap-4 p-4 rounded-lg bg-danger/10 border border-danger/20">
              <AlertTriangle className="w-6 h-6 text-danger flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-danger">{t('settings.withdrawAccount')}</h4>
                <p className="text-sm text-light-text-muted dark:text-dark-text-muted mt-1">
                  {t('settings.withdrawWarning')}
                </p>
                <button
                  onClick={() => setShowWithdrawModal(true)}
                  className="btn btn-danger mt-3 inline-flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('settings.withdrawAccount')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Withdraw Modal */}
      <Modal
        isOpen={showWithdrawModal}
        onClose={() => {
          setShowWithdrawModal(false)
          setWithdrawPassword('')
          setWithdrawError('')
        }}
        title={t('settings.withdrawAccount')}
      >
        <div className="space-y-4">
          <div className="p-4 rounded-lg bg-danger/10 border border-danger/20">
            <p className="text-sm text-danger">
              {t('settings.withdrawWarning')}
            </p>
          </div>

          <p className="text-sm text-light-text-muted dark:text-dark-text-muted">
            {t('settings.withdrawConfirm')}
          </p>

          <div>
            <label className="label">{t('auth.password')}</label>
            <input
              type="password"
              value={withdrawPassword}
              onChange={(e) => setWithdrawPassword(e.target.value)}
              className="input"
              placeholder={t('settings.enterPassword')}
            />
          </div>

          {withdrawError && (
            <p className="text-sm text-danger">{withdrawError}</p>
          )}

          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setShowWithdrawModal(false)}
              className="btn btn-secondary"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleWithdraw}
              disabled={isWithdrawing || !withdrawPassword}
              className="btn btn-danger"
            >
              {isWithdrawing ? '처리 중...' : t('settings.withdrawAccount')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
